import { useState } from "react";
import { ChevronDown, History, Pencil, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminApiConfigurations, useApiConfigurationAdmin, useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { ApiConfigurationItem } from "@/services/masterDataApi";
import { AdminAuditHistoryDialog } from "@/components/admin/AdminAuditHistoryDialog";

const PAGE_SIZE = 10;

const NONE_VALUE = "__none__";

const ApiConfigurations = () => {
  const { data: configs = [], isLoading } = useAdminApiConfigurations();
  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes = [] } = useAdminTemplateFormatTypes();
  const admin = useApiConfigurationAdmin();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiConfigurationItem | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [configuration, setConfiguration] = useState("");
  const [templateTypeId, setTemplateTypeId] = useState<string>(NONE_VALUE);
  const [selectedFormatTypeIds, setSelectedFormatTypeIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ApiConfigurationItem | null>(null);
  const [historyTarget, setHistoryTarget] = useState<ApiConfigurationItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(configs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = configs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageNumbers = (): (number | "ellipsis")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "ellipsis")[] = [1];
    if (currentPage > 3) pages.push("ellipsis");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  const openAdd = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setConfiguration(JSON.stringify({ api_url: "", queue_name: null }, null, 2));
    setTemplateTypeId(NONE_VALUE);
    setSelectedFormatTypeIds([]);
    setDialogOpen(true);
  };

  const openEdit = (item: ApiConfigurationItem) => {
    setEditing(item);
    setName(item.name);
    setDescription(item.description ?? "");
    setConfiguration(JSON.stringify(item.configuration, null, 2));
    setTemplateTypeId(item.template_type_id != null ? String(item.template_type_id) : NONE_VALUE);
    setSelectedFormatTypeIds(item.template_formattype_ids ?? []);
    setDialogOpen(true);
  };

  const toggleFormatType = (id: number) => {
    setSelectedFormatTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!name.trim() || !configuration || templateTypeId === NONE_VALUE || selectedFormatTypeIds.length === 0) return;

    // Validate and parse JSON configuration
    let configObj: { api_url: string; queue_name: string | null };
    try {
      configObj = JSON.parse(configuration);
    } catch (err) {
      alert("Invalid JSON in configuration field. Please check the format.");
      console.error("Configuration JSON parse error:", err);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        configuration: configObj,
        template_type_id: templateTypeId === NONE_VALUE ? null : Number(templateTypeId),
        template_formattype_ids: selectedFormatTypeIds.length > 0 ? selectedFormatTypeIds : null,
        is_active: editing?.is_active ?? true,
      };
      if (editing) {
        await admin.update.mutateAsync({ id: editing.id, data: payload });
      } else {
        await admin.create.mutateAsync(payload);
      }
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: ApiConfigurationItem) => {
    await admin.update.mutateAsync({ id: item.id, data: { is_active: !item.is_active } });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await admin.remove.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const getTemplateTypeName = (id: number | null) => {
    if (id == null) return null;
    return templateTypes.find((t) => t.id === id)?.name ?? String(id);
  };

  const getFormatTypeNames = (ids: number[] | null) => {
    if (!ids || ids.length === 0) return null;
    return ids.map((id) => formatTypes.find((f) => f.id === id)?.name ?? String(id)).join(", ");
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={openAdd} className="gap-1 bg-brand-teal text-white hover:bg-brand-teal/90">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Configuration</TableHead>
              <TableHead>Template Type</TableHead>
              <TableHead>Format Types</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No items yet.</TableCell>
              </TableRow>
            )}
            {pageItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">
                  <div>{item.name}</div>
                  {item.description && (
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</div>
                  )}
                </TableCell>
                <TableCell className="max-w-xs text-sm text-muted-foreground">
                  <div className="font-mono truncate">
                    {item.api_url ?? item.configuration?.api_url}
                  </div>
                  {(item.queue_name ?? item.configuration?.queue_name) && (
                    <div className="text-xs font-mono truncate text-muted-foreground/80">
                      Queue: {item.queue_name ?? item.configuration?.queue_name}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {getTemplateTypeName(item.template_type_id) ?? <span className="italic">—</span>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                  {getFormatTypeNames(item.template_formattype_ids) ?? <span className="italic">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={item.is_active ? "default" : "secondary"}>
                    {item.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(item)} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleToggleActive(item)}
                      title={item.is_active ? "Deactivate" : "Activate"}
                    >
                      {item.is_active
                        ? <ToggleRight className="w-4 h-4 text-brand-teal" />
                        : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setHistoryTarget(item)}
                      title="View History"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteTarget(item)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {getPageNumbers().map((p, i) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${i === 1 ? "start" : "end"}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === currentPage}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete API Configuration</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Are you sure you want to delete <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit API Configuration" : "Add API Configuration"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-semibold">Name <span className="text-destructive">*</span></Label>
              <Input
                className="mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <Label className="font-semibold">Description <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea
                className="mt-1.5 resize-y min-h-[72px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <Label className="font-semibold">Configuration <span className="text-destructive">*</span></Label>
              <Textarea
                className="mt-1.5 font-mono text-sm resize-y min-h-[100px]"
                value={configuration}
                onChange={(e) => setConfiguration(e.target.value)}
                placeholder='{"api_url": "http://localhost:8006", "queue_name": "pico-extraction-work-dev"}'
              />
            </div>           
            <div>
              <Label className="font-semibold">Template Type <span className="text-destructive">*</span></Label>
              <Select value={templateTypeId} onValueChange={setTemplateTypeId}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select a template type…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE} disabled>Select a template type…</SelectItem>
                  {templateTypes.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Template Format Types <span className="text-destructive">*</span></Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="mt-1.5 w-full justify-between font-normal text-left"
                  >
                    <span className="truncate">
                      {selectedFormatTypeIds.length === 0
                        ? "Select format types…"
                        : formatTypes
                            .filter((ft) => selectedFormatTypeIds.includes(ft.id))
                            .map((ft) => ft.name)
                            .join(", ")}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-2" align="start">
                  {formatTypes.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-2 py-1.5">No format types available.</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {formatTypes.map((ft) => (
                        <div
                          key={ft.id}
                          className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted cursor-pointer"
                          onClick={() => toggleFormatType(ft.id)}
                        >
                          <Checkbox
                            checked={selectedFormatTypeIds.includes(ft.id)}
                            onCheckedChange={() => toggleFormatType(ft.id)}
                          />
                          <span className="text-sm">{ft.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-brand-teal text-white hover:bg-brand-teal/90"
              onClick={handleSave}
              disabled={saving || !name.trim() || !configuration.trim() || templateTypeId === NONE_VALUE || selectedFormatTypeIds.length === 0}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdminAuditHistoryDialog
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        source="api_configuration"
        entityId={historyTarget?.id}
        entityName={historyTarget?.name}
        entityLabel="API Configuration"
      />
    </>
  );
};

export default ApiConfigurations;
